import styles from './index.module.scss';
import { Button, Tag, Typography } from '@navi/web-ui/lib/primitives';
import React, { useState } from 'react';
import GoogleMapIcon from '@cp/src/assets/icons/GoogleMapIcon';
import {
  openGoogleMapsWithAddress,
  openGoogleMapsWithCoordinates
} from '@cp/src/utils/commonUtils';
import { AddressCardHeaderProps, LocationSubType } from './interfaces';
import dayjs from 'dayjs';
import { DateFormat } from '@cp/src/utils/DateHelper';
import RecentActivityBadge from './AddressRecentActivity/RecentActivityBadge';
import { ThreeDotVerticalIcon } from '@navi/web-ui/lib/icons';
import { Tooltip, TooltipTrigger, TooltipContent } from '@cp/src/components/TooltipV2/TooltipV2';
import RecentActivityChangePopover from './AddressRecentActivity/RecentActivityChangePopover';
import { updateRecentActivity, fetchTopAddresses } from '../../actions/topAddressesActions';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { isLonghornAddress, isCustomerOutreachAddress } from './utils';

const CardHeader: React.FC<AddressCardHeaderProps> = props => {
  const { address } = props;
  const { loanId = '', customerId = '', caseReferenceId = '' } = useParams();
  const dispatch = useDispatch();
  const {
    city,
    pinCode,
    capturedAt,
    labels,
    locationSubType,
    latitude,
    longitude,
    addressText,
    metadata,
    referenceId
  } = address;

  const [isRecentActivityPopoverOpen, setIsRecentActivityPopoverOpen] = useState(false);
  const [isUpdatingRecentActivity, setIsUpdatingRecentActivity] = useState(false);

  const openMapHandler = () => {
    if (
      latitude &&
      longitude &&
      (locationSubType === LocationSubType.SKIP_TRACE_ADDRESS ||
        locationSubType === LocationSubType.GEO_LOCATION)
    ) {
      openGoogleMapsWithCoordinates(latitude, longitude);
      return;
    }
    openGoogleMapsWithAddress(addressText);
  };

  const handleThreeDotClick = () => {
    setIsRecentActivityPopoverOpen(prev => !prev);
  };

  const handleRecentActivityCancel = () => {
    setIsRecentActivityPopoverOpen(false);
  };

  const handleRecentActivitySave = async (date: Date) => {
    setIsUpdatingRecentActivity(true);
    try {
      const formattedDate = dayjs(date).format('YYYY-MM-DD');
      await updateRecentActivity(customerId, referenceId, formattedDate);
      toast.success('Recent activity updated successfully');
      setIsRecentActivityPopoverOpen(false);
      dispatch(fetchTopAddresses(loanId, customerId, caseReferenceId));
    } catch (error) {
      toast.error('Failed to update recent activity');
    } finally {
      setIsUpdatingRecentActivity(false);
    }
  };

  const hasRecentActivity = !!metadata?.recentActivityDate;
  const hasSimilarLocations = !!address?.similarLocationEntities?.length;
  const formattedDate = capturedAt
    ? dayjs(capturedAt).format(DateFormat.LONG_DATE_FORMAT_WITHOUT_TIME)
    : '--';
  const initialRecentActivityDate = metadata?.recentActivityDate
    ? new Date(metadata.recentActivityDate)
    : null;

  const showLonghorn = labels?.some(l => l.val === 'LONGHORN') || isLonghornAddress(address);
  const showCustomerOutreach =
    labels?.some(l => l.val === 'CUSTOMER_OUTREACH') || isCustomerOutreachAddress(address);

  const otherLabels = labels?.filter(l => !['LONGHORN', 'CUSTOMER_OUTREACH'].includes(l.val));

  const tagsElement = (
    <div className={styles.labels}>
      {otherLabels?.map(label => (
        <Tag
          key={label.val}
          variant="transparent"
          label={label.label}
          color={label.val === 'ALLOCATED_ADDRESS' ? 'primary' : 'violet'}
        />
      ))}
      {showLonghorn && <Tag key="LONGHORN" variant="transparent" label="Longhorn" color="violet" />}
      {showCustomerOutreach && (
        <Tag
          key="CUSTOMER_OUTREACH"
          variant="transparent"
          label="Customer Outreach"
          color="violet"
        />
      )}
    </div>
  );

  return (
    <div className={styles.header}>
      <div className={styles.headerTwoColumn}>
        {/* Left Column */}
        <div className={styles.headerLeftColumn}>
          <Typography variant="h3" color="var(--grayscale-content-2)">
            {pinCode || '--'}
          </Typography>
          <Typography variant="h3" color="var(--grayscale-content-2)">
            {city || '--'}
          </Typography>
          <Typography variant="p5" color="var(--grayscale-content-3)">
            {formattedDate}
          </Typography>
        </div>

        {/* Right Column */}
        <div className={styles.headerRightColumn}>
          <div className={styles.headerTopGroup}>
            <div className={styles.headerRightRow}>
              {hasRecentActivity ? <RecentActivityBadge metadata={metadata} /> : tagsElement}
              <Tooltip
                placement="bottom-end"
                open={isRecentActivityPopoverOpen}
                onOpenChange={open => !open && setIsRecentActivityPopoverOpen(false)}
              >
                <TooltipTrigger asChild>
                  {!hasSimilarLocations && (
                    <button
                      type="button"
                      onClick={handleThreeDotClick}
                      className={styles.threeDotButton}
                    >
                      <ThreeDotVerticalIcon color="var(--grayscale-content-2)" />
                    </button>
                  )}
                </TooltipTrigger>
                <TooltipContent bgColor="transparent">
                  <RecentActivityChangePopover
                    initialDate={initialRecentActivityDate}
                    onCancel={handleRecentActivityCancel}
                    onSave={handleRecentActivitySave}
                    isLoading={isUpdatingRecentActivity}
                  />
                </TooltipContent>
              </Tooltip>
            </div>
            {hasRecentActivity && <div className={styles.headerRightRow}>{tagsElement}</div>}
          </div>
          <div className={styles.headerRightRow}>
            <Button variant="text" onClick={openMapHandler} startAdornment={<GoogleMapIcon />}>
              Open Map
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardHeader;
